import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { useState, useEffect } from 'react';
import Heading from '@components/Heading/Heading';
import styles from '@features/showrooms/sections/ShowroomSection/Components/ShowroomCard/ShowroomCard.styles';
import Button from '@components/Button/Button';
import ShowroomGallery from '@features/showrooms/sections/ShowroomsSection/Components/ShowroomGallery/ShowroomGallery';
import { getShowroomName, navigationPossible, onClickNavigate, getNavigateUrl, getUserGeolocation, getShowroomReviewBoosterURL, getClearUserLocation, getOpeningHours } from '@utils/showroom';
import { IUserLocation, IPointOfPresenceWithDetails } from '@features/checkout/typescript';
import Icon from '@components/icons/Icon';
import sanitizeHtml from '@utils/sanitizeHtml';
type PropsType = {
  showroom: IPointOfPresenceWithDetails | null;
  offerButton: boolean;
  scrollToOffer: () => void;
};
let askUserLocation = false;
const ShowroomCard = ({
  showroom,
  offerButton,
  scrollToOffer
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const [userLocation, setUserLocation] = useState<IUserLocation>(getClearUserLocation());
  const [reviewBoosterURL, setReviewBoosterURL] = useState<string | undefined>(undefined);
  const CONFIG_SANITIZE = {
    allowedTags: ['strong', 'a', 'br'],
    allowedAttributes: {
      a: ['href', 'target', 'rel']
    }
  };
  const handleUserLocation = async () => {
    if (!askUserLocation) {
      askUserLocation = true;
      const newUserLocation = {
        ...userLocation
      };
      const newUserGeolocation = await getUserGeolocation();
      if (newUserGeolocation && newUserGeolocation?.lat && newUserGeolocation?.lng) {
        newUserLocation.location = newUserGeolocation;
      }
      setUserLocation(newUserLocation);
    }
  };
  const renderReviewBoosterURL = () => reviewBoosterURL ? <Button variant="outlined" variantColor="dark" size="small" href={reviewBoosterURL} openInNewTab>
        {t('button.showroomReviewBoosterURL')}
        <div>
          <Icon icon="Star" />
          <Icon icon="Star" />
          <Icon icon="Star" />
          <Icon icon="Star" />
          <Icon icon="Star" />
        </div>
      </Button> : null;
  useEffect(() => {
    handleUserLocation();
    if (showroom && !reviewBoosterURL) setReviewBoosterURL(getShowroomReviewBoosterURL(showroom));
  }, [showroom, reviewBoosterURL]);
  return <div sx={styles.showroomCard}>
      <div sx={styles.galleryContainer}>
        <Button variant="link" variantColor="white" size="small" href="/showrooms">
          <Icon icon="ArrowBack" />
          {t('button.backToShowroomsList')}
        </Button>
        {showroom?.gallery && <ShowroomGallery gallery={showroom?.gallery} />}
      </div>
      <div sx={styles.cardContainer}>
        {showroom && <>
            <Heading h={1} display={3} pb={40} mobile>
              {getShowroomName(showroom, t)}
              <span sx={styles.cardContainer.styledMainHeading}>
                {showroom?.address?.street}, {showroom?.address?.postalCode} {showroom?.address?.city},{' '}
                {showroom?.country?.name}
              </span>
            </Heading>
          </>}
        <div sx={styles.cardContainer.details}>
          <div sx={styles.cardContainer.openingHours}>
            {showroom?.openingHours?.description && getOpeningHours(showroom, t).split('\n').map(desriptionItem => <span key={desriptionItem}>{desriptionItem}</span>)}
          </div>
          {showroom?.contact?.emails && showroom.contact.emails.length > 0 && showroom.contact.emails.map(email => email && <div sx={styles.cardContainer.emailLink} key={email}>
                    <Link href={`mailto:${email}`}>
                      <span>{email}</span>
                    </Link>
                  </div>)}
          {showroom?.contact?.phones && showroom.contact.phones.length > 0 && showroom.contact.phones.map(phone => {
          if (!phone) return null;
          return <div sx={styles.cardContainer.phoneLink} key={phone}>
                  <Link href={`tel:${phone.replaceAll(' ', '')}`}>
                    <Heading h={5} headingType={5} uppercase>
                      {phone}
                    </Heading>
                  </Link>
                </div>;
        })}
        </div>
        <div sx={styles.cardContainer.buttons()}>
          {offerButton && <Button variant="default" variantColor="dark" size="small" onClick={scrollToOffer}>
              {t('button.showroomSeeOffer')}
            </Button>}
          {navigationPossible(showroom?.location) && (() => {
          const navigateHref = getNavigateUrl(showroom?.location, userLocation?.location ?? null);
          return navigateHref ? <Button variant="outlined" variantColor="dark" size="small" href={navigateHref} openInNewTab>
                  {t('button.navigate')}
                </Button> : <Button variant="outlined" variantColor="dark" size="small" onClick={() => onClickNavigate(showroom?.location, userLocation?.location)}>
                  {t('button.navigate')}
                </Button>;
        })()}
          {!navigationPossible(showroom?.location) && reviewBoosterURL && renderReviewBoosterURL()}
        </div>
        {navigationPossible(showroom?.location) && reviewBoosterURL && <div sx={styles.cardContainer.buttons(true)}>{renderReviewBoosterURL()}</div>}
        <p dangerouslySetInnerHTML={sanitizeHtml(showroom?.description?.summary || '', CONFIG_SANITIZE)}></p>
      </div>
    </div>;
};
export default ShowroomCard;